/**
 * Per-position edit dialog — opened via the chart's right-click
 * context menu on a position handle ("Edit settings"). Lets the user
 * type entry / SL / TP prices, and surfaces the derived risk metrics
 * (R:R, % moves, raw distance) read-only.
 *
 * State pattern mirrors FibLevelsDialog: visibility comes from
 * `ui.positionDialogId` (null = closed), the draft lives locally,
 * Save dispatches `updatePosition` and clears the dialog id.
 */
import { useEffect, useState } from "react";
import {
  useAppDispatch,
  useAppSelector,
  useActiveTabId,
  useActiveUi,
} from "@/state/hooks";
import { setPositionDialogId, updatePosition } from "@/state/slices/uiSlice";
import styles from "./PositionDialog.module.css";

export function PositionDialog() {
  const dispatch = useAppDispatch();
  // The dialog is shared across tabs; positionDialogId is global but
  // positions are per-tab, so the dialog only meaningfully renders for
  // a position in the *active* tab. On tab switch, the dialog hides
  // automatically because positions.find() returns null.
  const tabId = useActiveTabId();
  const positionId = useAppSelector((s) => s.ui.positionDialogId);
  const { positions } = useActiveUi();
  const position = positions.find((p) => p.id === positionId) ?? null;

  // Local draft as strings — numeric inputs need to allow intermediate
  // states like "" or "4687." while typing without snapping to NaN.
  // Synced to the underlying position whenever the dialog opens for a
  // new id.
  const [entryStr, setEntryStr] = useState("");
  const [slStr, setSlStr] = useState("");
  const [tpStr, setTpStr] = useState("");

  useEffect(() => {
    if (position) {
      setEntryStr(position.entryPrice.toString());
      setSlStr(position.slPrice.toString());
      setTpStr(position.tpPrice.toString());
    }
  }, [positionId, position]);

  if (!position) return null;

  const entry = Number.parseFloat(entryStr);
  const sl = Number.parseFloat(slStr);
  const tp = Number.parseFloat(tpStr);
  const allValid = Number.isFinite(entry) && Number.isFinite(sl) && Number.isFinite(tp);

  // Derived metrics use the current draft so the user sees R:R update
  // live as they type — important feedback when sizing a trade.
  const reward = allValid ? Math.abs(tp - entry) : 0;
  const risk = allValid ? Math.abs(entry - sl) : 0;
  const rr = risk > 0 ? reward / risk : 0;
  const tpPct = allValid && entry ? ((tp - entry) / entry) * 100 : 0;
  const slPct = allValid && entry ? ((sl - entry) / entry) * 100 : 0;

  const handleClose = () => dispatch(setPositionDialogId(null));

  const handleSave = () => {
    if (!allValid) return;
    dispatch(
      updatePosition({
        tabId,
        id: position.id,
        entryPrice: entry,
        slPrice: sl,
        tpPrice: tp,
      }),
    );
    handleClose();
  };

  return (
    <div
      className={styles.backdrop}
      onClick={handleClose}
      role="presentation"
    >
      <div
        className={styles.dialog}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className={styles.header}>
          <h2 className={styles.title}>
            Position
            <span
              className={`${styles.badge} ${
                position.type === "long" ? styles.badgeLong : styles.badgeShort
              }`}
            >
              {position.type.toUpperCase()}
            </span>
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className={styles.body}>
          <div className={styles.row}>
            <label htmlFor="pos-tp" className={`${styles.label} ${styles.labelTp}`}>
              Take profit
            </label>
            <input
              id="pos-tp"
              type="number"
              step="0.001"
              className={styles.numInput}
              value={tpStr}
              onChange={(e) => setTpStr(e.target.value)}
            />
          </div>
          <div className={styles.row}>
            <label
              htmlFor="pos-entry"
              className={`${styles.label} ${styles.labelEntry}`}
            >
              Entry
            </label>
            <input
              id="pos-entry"
              type="number"
              step="0.001"
              className={styles.numInput}
              value={entryStr}
              onChange={(e) => setEntryStr(e.target.value)}
            />
          </div>
          <div className={styles.row}>
            <label htmlFor="pos-sl" className={`${styles.label} ${styles.labelSl}`}>
              Stop loss
            </label>
            <input
              id="pos-sl"
              type="number"
              step="0.001"
              className={styles.numInput}
              value={slStr}
              onChange={(e) => setSlStr(e.target.value)}
            />
          </div>

          <div className={styles.metrics}>
            <span className={styles.metricLabel}>Risk : Reward</span>
            <span className={styles.metricValue}>{rr.toFixed(2)}</span>

            <span className={styles.metricLabel}>TP distance</span>
            <span
              className={`${styles.metricValue} ${
                tpPct >= 0 ? styles.metricPositive : styles.metricNegative
              }`}
            >
              {tpPct >= 0 ? "+" : ""}
              {tpPct.toFixed(2)}% · {reward.toFixed(3)}
            </span>

            <span className={styles.metricLabel}>SL distance</span>
            <span
              className={`${styles.metricValue} ${
                slPct >= 0 ? styles.metricPositive : styles.metricNegative
              }`}
            >
              {slPct >= 0 ? "+" : ""}
              {slPct.toFixed(2)}% · {risk.toFixed(3)}
            </span>
          </div>
        </div>

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={handleSave}
            disabled={!allValid}
          >
            Save
          </button>
        </footer>
      </div>
    </div>
  );
}
